if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;

        this.updateResourceResults(this.getQuery());
      }

      async updateResourceResults(query) {
        if (!query || query.length < 2) return;
        this.resourceQueryId = (this.resourceQueryId || 0) + 1;
        const currentReqId = this.resourceQueryId;

        const resultsId = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const resultsEl = document.getElementById(resultsId);
        if (!resultsEl) return;

        try {
          const resources = await window.KascoResourceSearch.search(query, 5);
          if (this.resourceQueryId !== currentReqId || this.getQuery() !== query) return;

          resultsEl.querySelectorAll('.predictive-search-resources, .predictive-search-resources-hub-link').forEach((el) => el.remove());

          if (resources && resources.length > 0) {
            const emptyState = resultsEl.querySelector('.search__empty');
            if (emptyState) {
              emptyState.style.display = 'none';
            }

            const resourceSection = document.createElement('div');
            resourceSection.className = 'predictive-search-resources grid gap-3 md:gap-4 w-full';

            let itemsHtml = '';
            resources.forEach((file) => {
              const badge = file.category_title
                ? `<span class="predictive-resource-badge">${escapeHtml(file.category_title)}</span>`
                : '';
              const subTitle = file.subcategory_title
                ? `<span class="text-xs opacity-70 truncate">${escapeHtml(file.subcategory_title)}</span>`
                : '';

              const viewBtn = file.is_pdf
                ? `<a href="${file.view_url}" target="_blank" class="button button--secondary button--small" role="link" is="hover-link">
                    <span class="btn-fill" data-fill=""></span>
                    <span class="btn-text">View</span>
                  </a>`
                : '';

              const downloadBtn = `
                <a href="${file.download_url}" target="_blank" class="button button--primary button--small icon-with-text" role="link" is="hover-link">
                  <span class="btn-fill" data-fill=""></span>
                  <span class="btn-text">
                    Download
                    <svg class="icon icon-download icon-2xs transform" viewBox="160 -800 640 640" stroke="none" fill="currentColor">
                      <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"></path>
                    </svg>
                  </span>
                </a>
              `;

              itemsHtml += `
                <li class="predictive-resource-item">
                  <div class="flex flex-col gap-1 overflow-hidden" style="flex: 1 1 auto; min-width: 0;">
                    <div class="flex items-center gap-2">
                      ${badge}
                      ${subTitle}
                    </div>
                    <a href="${file.download_url}" target="_blank" class="font-medium text-sm truncate hover:underline" style="font-weight: 600; text-decoration: none;">
                      <svg style="width: 16px; height: 16px; display: inline-block; vertical-align: text-bottom; margin-right: 6px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                      ${escapeHtml(file.title)}
                    </a>
                  </div>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    ${viewBtn}
                    ${downloadBtn}
                  </div>
                </li>
              `;
            });

            resourceSection.innerHTML = `
              <div class="flex items-center justify-between w-full" style="display: flex; justify-content: space-between; align-items: center;">
                <p class="search__heading block text-xs uppercase leading-tight tracking-widest font-semibold" style="margin: 0; letter-spacing: 0.08em;">
                  Manuals &amp; Documents (${resources.length})
                </p>
                <a href="/pages/downloadable-resources?q=${encodeURIComponent(query)}" class="text-xs font-semibold hover:underline" style="font-size: 0.75rem; text-decoration: underline;">
                  View all in Hub &rarr;
                </a>
              </div>
              <ul class="grid gap-2 w-full" style="list-style: none; padding: 0; margin: 0;">
                ${itemsHtml}
              </ul>
            `;

            const stickyBtn = resultsEl.querySelector('.drawer__sticky');
            if (stickyBtn) {
              resultsEl.insertBefore(resourceSection, stickyBtn);
            } else {
              resultsEl.appendChild(resourceSection);

              const hubBtn = document.createElement('div');
              hubBtn.className = 'predictive-search-resources-hub-link drawer__sticky z-1 sticky left-0 bottom-0 w-full mt-4';
              hubBtn.innerHTML = `
                <a class="button button--secondary icon-with-text w-full" href="/pages/downloadable-resources?q=${encodeURIComponent(query)}" role="link" is="hover-link">
                  <span class="btn-fill" data-fill=""></span>
                  <span class="btn-text">
                    Browse All Resources in Hub
                    <svg class="icon icon-arrow-right icon-sm transform" viewBox="0 0 21 20" stroke="currentColor" fill="none">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 10H18M18 10L12.1667 4.16675M18 10L12.1667 15.8334"></path>
                    </svg>
                  </span>
                </a>
              `;
              resultsEl.appendChild(hubBtn);
            }

            initWaveButtons(resultsEl);
            this.setAttribute('results', '');
          }
        } catch (err) {
          console.warn('Error loading predictive search resources:', err);
        }
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}

function initWaveButtons(root) {
  if (!root) return;
  const buttons = root.querySelectorAll('.button, [is="hover-link"], [is="hover-button"]');
  buttons.forEach((btn) => {
    if (btn._hoverWaveInitialized) return;
    btn._hoverWaveInitialized = true;

    let fill = btn.querySelector('[data-fill]');
    if (!fill) {
      fill = document.createElement('span');
      fill.className = 'btn-fill';
      fill.setAttribute('data-fill', '');
      btn.insertBefore(fill, btn.firstChild);
    }

    const motionAnimate = (window.Motion && window.Motion.animate) || (typeof Motion !== 'undefined' && Motion.animate);
    if (motionAnimate) {
      btn.addEventListener('mouseenter', () => {
        motionAnimate(fill, { y: ['76%', '0%'] }, { duration: 0.6 });
      });
      btn.addEventListener('mouseleave', () => {
        motionAnimate(fill, { y: '-76%' }, { duration: 0.6 });
      });
    } else if (window.theme && window.theme.HoverButton) {
      const hb = new window.theme.HoverButton(btn);
      hb.load();
    }
  });
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

window.KascoResourceSearch = window.KascoResourceSearch || {
  treeCache: null,
  treePromise: null,

  async fetchPublishedTree() {
    if (this.treeCache) return this.treeCache;
    if (this.treePromise) return this.treePromise;

    try {
      const stored = sessionStorage.getItem('kasco_published_resources');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && Array.isArray(parsed.tree) && Date.now() - (parsed.time || 0) < 30 * 60 * 1000) {
          this.treeCache = parsed.tree;
          return this.treeCache;
        }
      }
    } catch (e) {}

    this.treePromise = fetch('/apps/erp-account/api/resources/published')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(res.statusText))))
      .then((data) => {
        this.treeCache = data.tree || [];
        try {
          sessionStorage.setItem('kasco_published_resources', JSON.stringify({ tree: this.treeCache, time: Date.now() }));
        } catch (e) {}
        this.treePromise = null;
        return this.treeCache;
      })
      .catch((err) => {
        this.treePromise = null;
        console.warn('Could not load published resources tree', err);
        return [];
      });

    return this.treePromise;
  },

  filterFromTree(tree, query, limit = 6) {
    if (!query || !query.trim()) return [];
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];

    const allFiles = [];
    tree.forEach((cat) => {
      (cat.files || []).forEach((f) => {
        allFiles.push({
          id: f.id,
          title: f.title,
          category_id: cat.id,
          category_title: cat.title,
          subcategory_title: '',
          shopify_url: f.shopify_url,
          product_handle: f.product_handle || ''
        });
      });
      (cat.subcategories || []).forEach((sub) => {
        (sub.files || []).forEach((f) => {
          allFiles.push({
            id: f.id,
            title: f.title,
            category_id: cat.id,
            category_title: cat.title,
            subcategory_title: sub.title,
            shopify_url: f.shopify_url,
            product_handle: f.product_handle || ''
          });
        });
      });
    });

    return allFiles
      .filter((f) => {
        const title = (f.title || '').toLowerCase();
        const handle = (f.product_handle || '').toLowerCase();
        const catTitle = (f.category_title || '').toLowerCase();
        const subTitle = (f.subcategory_title || '').toLowerCase();

        return terms.every((term) => {
          const tClean = term.toLowerCase();
          const tNoDash = tClean.replace(/-/g, '');
          return (
            title.includes(tClean) ||
            title.replace(/-/g, '').includes(tNoDash) ||
            handle.includes(tClean) ||
            handle.replace(/-/g, '').includes(tNoDash) ||
            catTitle.includes(tClean) ||
            subTitle.includes(tClean)
          );
        });
      })
      .slice(0, limit)
      .map((f) => {
        const isPdf = Boolean(f.shopify_url && f.shopify_url.toLowerCase().endsWith('.pdf'));
        let viewUrl = f.shopify_url;
        if (isPdf && viewUrl.startsWith('/public/')) {
          viewUrl = '/apps/erp-account' + viewUrl;
        }
        return {
          id: f.id,
          title: f.title,
          category_title: f.category_title,
          subcategory_title: f.subcategory_title,
          download_url: `/apps/erp-account/api/resources/download/${f.id}`,
          view_url: viewUrl,
          is_pdf: isPdf
        };
      });
  },

  async search(query, limit = 6) {
    if (!query || !query.trim()) return [];
    const trimmed = query.trim();

    try {
      const res = await fetch(`/apps/erp-account/api/resources/search?q=${encodeURIComponent(trimmed)}&limit=${limit}`);
      if (res.ok) {
        const data = await res.json();
        if (data && Array.isArray(data.resources)) {
          return data.resources;
        }
      }
    } catch (e) {}

    const tree = await this.fetchPublishedTree();
    return this.filterFromTree(tree, trimmed, limit);
  }
};

function initSearchPageResources() {
  const container = document.getElementById('SearchResourceResults');
  if (!container) return;

  const query = container.getAttribute('data-query');
  if (!query || !query.trim()) return;

  window.KascoResourceSearch.search(query.trim(), 20).then((resources) => {
    if (!resources || resources.length === 0) return;

    const emptyState = document.querySelector('.collection .empty-state');
    if (emptyState) {
      emptyState.style.display = 'none';
    }

    let cardsHtml = '';
    resources.forEach((file) => {
      const badge = file.category_title ? `<span class="predictive-resource-badge">${escapeHtml(file.category_title)}</span>` : '';
      const subTitle = file.subcategory_title ? `<span class="text-xs opacity-75">${escapeHtml(file.subcategory_title)}</span>` : '';

      const viewBtn = file.is_pdf
        ? `<a href="${file.view_url}" target="_blank" class="button button--secondary button--sm" role="link" is="hover-link"><span class="btn-fill" data-fill></span><span class="btn-text">View</span></a>`
        : '';

      const downloadBtn = `
        <a href="${file.download_url}" target="_blank" class="button button--primary button--sm icon-with-text" role="link" is="hover-link">
          <span class="btn-fill" data-fill></span>
          <span class="btn-text">
            Download
            <svg class="icon icon-download icon-2xs transform" viewBox="160 -800 640 640" stroke="none" fill="currentColor">
              <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"></path>
            </svg>
          </span>
        </a>
      `;

      cardsHtml += `
        <div class="search-resource-card">
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2">
              ${badge}
              ${subTitle}
            </div>
            <h3 class="text-base lg:text-lg font-semibold leading-snug">
              <a href="${file.download_url}" target="_blank" class="hover:underline">
                <svg style="width: 18px; height: 18px; display: inline-block; vertical-align: text-bottom; margin-right: 6px;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                ${escapeHtml(file.title)}
              </a>
            </h3>
          </div>
          <div class="flex items-center gap-3 mt-auto pt-2">
            ${viewBtn}
            ${downloadBtn}
          </div>
        </div>
      `;
    });

    container.innerHTML = `
      <div class="search-resource-container">
        <div class="search-resource-header">
          <div class="search-resource-header__info">
            <h2 class="search-resource-header__title heading text-xl lg:text-2xl font-bold leading-tight">
              Manuals &amp; Technical Documents (${resources.length})
            </h2>
            <p class="search-resource-header__sub">Downloadable spec sheets, manuals, and literature matching "${escapeHtml(query)}"</p>
          </div>
          <a href="/pages/downloadable-resources?q=${encodeURIComponent(query)}" class="button button--secondary button--sm" role="link" is="hover-link">
            <span class="btn-fill" data-fill=""></span>
            <span class="btn-text">Browse All in Hub &rarr;</span>
          </a>
        </div>
        <div class="search-resource-grid">
          ${cardsHtml}
        </div>
      </div>
    `;

    initWaveButtons(container);
  }).catch((err) => {
    console.warn('Error loading search page resources:', err);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSearchPageResources);
} else {
  initSearchPageResources();
}

document.addEventListener('page:loaded', initSearchPageResources);

if (window.theme && window.theme.pubsub && window.theme.pubsub.PUB_SUB_EVENTS) {
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, () => {
    initSearchPageResources();
  });
}

